import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import debounce from "lodash/debounce";
import { Divider, Form, Header } from "semantic-ui-react";

import AlternateNameView from "components/AlternateName";
import Addresses from "components/forms/addresses/Addresses";
import CustomFieldForm from "components/forms/CustomFieldForm";
import {
  checkIsCustomFieldsReady,
  extractCustom,
  getCustomFieldsForSubmit,
} from "components/helpers";
import RuvixxForm from "components/RuvixxForm";
import { mapCustomFieldValues } from "helpers/customFields";
import CustomFieldService from "services/CustomField";
import EntityService from "services/Entity";
import UserService from "services/User";
import RegionService from "services/Regions";
import { getCountryByCode } from "helpers/countries";
import { filterUsersForDropdown } from "helpers/user";
import { TagFormField } from "components/TagFormField";
import tagConstants from "constants/Tag";

const EntityForm = ({ entityId, onSuccess }) => {
  const isEditMode = !!entityId;

  // Form data
  const [name, setName] = useState("");
  const [urls, setUrls] = useState([]);
  const [externalCcEmails, setExternalCcEmails] = useState("");
  const [accountManagers, setAccountManagers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [alternateNames, setAlternateNames] = useState([]);
  const [custom, setCustom] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [country, setCountry] = useState(null);
  const [primaryAddressId, setPrimaryAddressId] = useState(null);
  const [tags, setTags] = useState([]);

  // Options and configs
  const [allUsers, setAllUsers] = useState([]);
  const [customFieldConfigs, setCustomFieldConfigs] = useState([]);
  const [regionsOptions, setRegionsOptions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [websiteOptions, setWebsiteOptions] = useState([]);

  // Loading states
  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);
  const [entityLoaded, setEntityLoaded] = useState(!isEditMode);

  // Ref to skip fetchCountries effect during initial entity load
  const isInitialLoadRef = useRef(isEditMode);

  // Fetch entity data (edit mode only)
  const fetchEntity = useCallback(async () => {
    if (!entityId) return;

    const entity = await EntityService.getEntity(entityId);
    const {
      name,
      urls,
      external_cc_emails,
      addresses,
      account_managers,
      custom_fields,
      alternate_names,
      region_id,
      country,
      primary_address_id,
      tags,
    } = entity;

    setName(name);
    setUrls(urls || []);
    setExternalCcEmails(external_cc_emails || "");
    setAccountManagers(account_managers.map(m => m.user_id));
    setAlternateNames(alternate_names || []);
    setPrimaryAddressId(primary_address_id);
    setTags(tags || []);

    // Populate countries options BEFORE setting region and country
    // This ensures the Form.Select can display the country value
    if (region_id) {
      const region = await RegionService.getRegion(region_id);
      const options =
        region?.countries.map(code => ({
          key: code,
          value: code,
          text: getCountryByCode(code).text,
        })) || [];
      setCountriesOptions(options);
    }

    setRegionId(region_id);
    setCountry(country);

    // Prepare addresses with primary flag
    const preparedAddresses = addresses || [];
    if (primary_address_id) {
      preparedAddresses.forEach(addr => {
        addr.is_primary = addr.id === primary_address_id;
      });
    }
    setAddresses(preparedAddresses);

    // Handle custom fields
    const customData = extractCustom(custom_fields);
    setCustom(customData);

    // Fetch and map custom field configs
    const configs =
      await CustomFieldService.getCustomFieldConfigsForModel("Entity");
    mapCustomFieldValues(customData, configs);
    setCustomFieldConfigs(configs);
    setCustomFieldsLoaded(true);
    setEntityLoaded(true);

    // Mark initial load as complete so the fetchCountries effect can run normally
    isInitialLoadRef.current = false;
  }, [entityId]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    const rawUsers = await UserService.getUsers();
    setAllUsers(rawUsers);
  }, []);

  // Derive filtered users from allUsers and accountManagers
  const users = useMemo(
    () => filterUsersForDropdown(allUsers, accountManagers),
    [allUsers, accountManagers]
  );

  // Fetch custom field configs (create mode only)
  const fetchCustomFieldConfigs = useCallback(async () => {
    if (isEditMode) return; // Edit mode handles this in fetchEntity
    const configs =
      await CustomFieldService.getCustomFieldConfigsForModel("Entity");
    setCustomFieldConfigs(configs);
    setCustomFieldsLoaded(true);
  }, [isEditMode]);

  // Fetch regions
  const fetchRegions = useCallback(async () => {
    const regionsData = await RegionService.getRegions();
    const options = regionsData.map(({ id, name }) => ({
      key: id,
      value: id,
      text: name,
    }));
    setRegionsOptions(options);
    setRegions(regionsData);
  }, []);

  // Fetch countries for selected region
  const fetchCountries = useCallback(
    async regionId => {
      if (!regionId) {
        setCountriesOptions([]);
        return;
      }
      const region = await RegionService.getRegion(regionId);
      const options =
        region?.countries.map(code => ({
          key: code,
          value: code,
          text: getCountryByCode(code).text,
        })) || [];
      setCountriesOptions(options);

      // Clear country if not in new region
      if (region && !region.countries.includes(country)) {
        setCountry(null);
      }
    },
    [country]
  );

  // Debounced search for website URL suggestions
  const handleUrlSearchChange = useMemo(
    () =>
      debounce(async (_e, { searchQuery }) => {
        if (!searchQuery) {
          setWebsiteOptions([]);
          return;
        }
        const results = await EntityService.getWebsitesForFilters({
          search_query: searchQuery,
        });
        setWebsiteOptions(
          results.map(({ name }) => ({ key: name, value: name, text: name }))
        );
      }, 300),
    []
  );

  // Handle form field changes
  const handleChange = (_, { name, value }) => {
    switch (name) {
      case "name":
        setName(value);
        break;
      case "urls":
        setUrls(value);
        break;
      case "externalCcEmails":
        setExternalCcEmails(value);
        break;
      case "accountManagers":
        setAccountManagers(value);
        break;
      case "regionId":
        setRegionId(value);
        break;
      case "country":
        setCountry(value);
        break;
      default:
        break;
    }
  };

  // Handle custom field config updates
  const handleUpdateCustomFieldConfigs = configs => {
    setCustomFieldConfigs(configs);
    setCustom(
      configs
        .filter(cfc => cfc.required || cfc.selected)
        .map(cfc => ({ key: cfc.label, value: cfc.value }))
    );
  };

  // Check if address is complete
  const isAddressReady = address => {
    return (
      address.street1 &&
      address.city &&
      address.region &&
      address.postal_code &&
      address.country
    );
  };

  // Check if all addresses are ready
  const checkAddressesReady = () => {
    return addresses.every(address => isAddressReady(address));
  };

  // Handle primary address toggle
  const updatePrimaryAddress = (address, index, checked) => {
    if (!isAddressReady(address)) return;

    const addressesCopy = addresses.map((addr, i) => ({
      ...addr,
      is_primary: i === index ? checked : false,
    }));
    setAddresses(addressesCopy);

    if (primaryAddressId === address.id) {
      setPrimaryAddressId(null);
    } else {
      setPrimaryAddressId(address.id);

      // Auto-set region/country from primary address
      const regionsFound = regions.filter(region =>
        region.countries.includes(address.country)
      );
      if (regionsFound?.length) {
        if (!country) {
          setCountry(address.country);
        }
        if (!regionId) {
          setRegionId(regionsFound[0].id);
        }
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const cust = getCustomFieldsForSubmit(customFieldConfigs, custom);
    const urlsArray = urls || [];

    if (isEditMode) {
      await EntityService.editEntity(entityId, {
        name,
        urls: urlsArray,
        external_cc_emails: externalCcEmails,
        addresses,
        custom_fields: cust,
        account_managers: accountManagers,
        alternate_names: alternateNames,
        region_id: regionId || null,
        country: country || null,
        tags,
      });
    } else {
      await EntityService.createEntity({
        name,
        urls: urlsArray,
        external_cc_emails: externalCcEmails,
        addresses,
        custom_fields: cust,
        account_managers: accountManagers,
        alternate_names: alternateNames,
        region_id: regionId || null,
        country: country || null,
        tags,
      });
    }
  };

  const handleTagChange = nextTags => setTags(nextTags);

  const isReady =
    !!name &&
    name.trim() !== "" &&
    checkIsCustomFieldsReady(customFieldConfigs) &&
    customFieldsLoaded &&
    entityLoaded &&
    (isEditMode ? checkAddressesReady() : true);

  // Initial data fetch
  useEffect(() => {
    (async () => {
      await fetchUsers();
      await fetchRegions();
      if (isEditMode) {
        await fetchEntity();
      } else {
        await fetchCustomFieldConfigs();
      }
    })();
  }, [
    fetchUsers,
    fetchRegions,
    fetchEntity,
    fetchCustomFieldConfigs,
    isEditMode,
  ]);

  // Update countries when region changes
  // Skip during initial entity load since fetchEntity already handles it
  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }
    (async () => {
      await fetchCountries(regionId);
    })();
  }, [regionId, fetchCountries]);

  useEffect(() => {
    return () => {
      handleUrlSearchChange.cancel();
    };
  }, [handleUrlSearchChange]);

  return (
    <RuvixxForm
      ready={isReady}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      successHeader={isEditMode ? "Entity Updated" : "Entity Created"}
      successMessage={`Successfully ${
        isEditMode ? "updated" : "created"
      } the ${name} entity`}
    >
      <Header className="section-header">Basic Info</Header>
      <Form.Input
        inline
        className="fillSpace"
        name="name"
        label="Name"
        required
        value={name}
        onChange={handleChange}
      />
      <Form.Field>
        <AlternateNameView
          alternateNames={alternateNames}
          setAlternateNames={setAlternateNames}
          type="Entity"
        />
      </Form.Field>
      <Form.Select
        selectOnBlur={false}
        inline
        className="fillSpace"
        clearable
        search
        multiple
        label="URLs"
        name="urls"
        placeholder="Add website URL..."
        options={[
          ...(urls || []).map(url => ({ key: url, value: url, text: url })),
          ...websiteOptions.filter(opt => !(urls || []).includes(opt.value)),
        ]}
        value={urls || []}
        onChange={handleChange}
        onSearchChange={handleUrlSearchChange}
        allowAdditions
      />
      <Form.Select
        selectOnBlur={false}
        label="Account Managers"
        clearable
        name="accountManagers"
        inline
        className="fillSpace"
        multiple
        search
        selection
        onChange={handleChange}
        options={users}
        value={accountManagers}
      />
      <Form.Input
        inline
        className="fillSpace"
        name="externalCcEmails"
        label="External CC Emails"
        placeholder="email1@test.com,email2@test.com"
        value={externalCcEmails}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        label="Region"
        name="regionId"
        search
        clearable
        options={regionsOptions}
        value={regionId}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        label="Country"
        name="country"
        disabled={!regionId}
        search
        clearable
        options={countriesOptions}
        value={country}
        onChange={handleChange}
      />
      <Addresses
        addresses={addresses}
        updateAddresses={setAddresses}
        showPrimaryColumn={true}
        primaryAddressId={primaryAddressId}
        updatePrimaryAddress={updatePrimaryAddress}
        showLatLong
      />
      <Divider hidden />
      <TagFormField
        modelType={tagConstants.taggableModels.entity}
        onChange={handleTagChange}
        formValue={tags}
      />
      {customFieldConfigs.length > 0 && (
        <CustomFieldForm
          modelType="Entity"
          customFields={custom}
          customFieldConfigs={customFieldConfigs}
          onUpdateCustomFieldConfigs={handleUpdateCustomFieldConfigs}
        />
      )}
    </RuvixxForm>
  );
};

export default EntityForm;
